import React from 'react';
import {
  ArrowLeft,
  MapPin,
  MoreHorizontal,
  RotateCcw,
  Check,
  X,
  FilePlus,
  Phone,
  Navigation,
  Pencil,
  FileText,
  Package,
  Paperclip,
  Receipt,
} from 'lucide-react';
import { Lead, contactDisplayName } from '../../../../types';
import { LeadCategoryPicker, type LeadCategoryOption } from '../../../../components/LeadCategoryPicker';
import { leadHeaderEurFmt } from './leadActivityHelpers';
import { useModuleAccess } from '../../../../hooks/useModuleAccess';
import {
  projectValueContextText,
  projectValueShortLabel,
  projectValueTooltip,
  type ProjectValue,
} from '../../utils/projectValue';

/** Mobiler Header: Karte + schwebende Kontakt-Karte, Deal-Infoleiste, Mobile-Tabs. */
export function LeadDetailMobileHero({
  lead,
  goBack,
  showMobileActionsMenu,
  setShowMobileActionsMenu,
  isLeadWon,
  isLeadLost,
  isSaving,
  handleReopenLead,
  handleGewonnen,
  openVerlorenModal,
  setShowCreateDocumentModal,
  baustelleMapAddress,
  headerContactName,
  headerContactCompany,
  headerCallPhone,
  terminalStatusLabel,
  status,
  leadCategories,
  handleLeadCategoryChange,
  leadTitleEditing,
  leadTitleDraft,
  setLeadTitleDraft,
  leadTitleInputMobileRef,
  saveLeadTitleFromEdit,
  cancelLeadTitleEdit,
  startEditingLeadTitle,
  offerNetSum,
  projectValue,
  activeMobileSection,
  setActiveMobileSection,
}: {
  lead: Lead;
  goBack: () => void;
  showMobileActionsMenu: boolean;
  setShowMobileActionsMenu: React.Dispatch<React.SetStateAction<boolean>>;
  isLeadWon: boolean;
  isLeadLost: boolean;
  isSaving: boolean;
  handleReopenLead: () => void | Promise<void>;
  handleGewonnen: () => void | Promise<void>;
  openVerlorenModal: () => void;
  setShowCreateDocumentModal: React.Dispatch<React.SetStateAction<boolean>>;
  baustelleMapAddress: string;
  headerContactName: string;
  headerContactCompany: string;
  headerCallPhone: string;
  terminalStatusLabel: string | null;
  status: string;
  leadCategories: LeadCategoryOption[];
  handleLeadCategoryChange: (key: string) => void;
  leadTitleEditing: boolean;
  leadTitleDraft: string;
  setLeadTitleDraft: React.Dispatch<React.SetStateAction<string>>;
  leadTitleInputMobileRef: React.RefObject<HTMLInputElement | null>;
  saveLeadTitleFromEdit: () => void | Promise<void>;
  cancelLeadTitleEdit: () => void;
  startEditingLeadTitle: () => void;
  offerNetSum: number | null;
  projectValue: ProjectValue | null;
  activeMobileSection: 'details' | 'zusammenfassung' | 'angebot' | 'dokumente' | 'kosten';
  setActiveMobileSection: React.Dispatch<
    React.SetStateAction<'details' | 'zusammenfassung' | 'angebot' | 'dokumente' | 'kosten'>
  >;
}) {
  // Kosten-Tab zeigt das Controlling aus den Rechnungen.
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  const showKostenTab = modulesLoaded && isEnabled('rechnungen');
  // Positionen und Angebote nur mit dem Modul Angebote.
  const showAngebotTab = modulesLoaded && isEnabled('angebote');
  return (
    <div className="md:hidden flex flex-col bg-app-surface-muted">
      {/* Map + floating card wrapper */}
      <div className="relative">
        {/* Map */}
        <div className="w-full h-52 bg-slate-200 overflow-hidden shrink-0">
          {baustelleMapAddress ? (
            <iframe
              title="Karte Baustelle"
              src={`https://www.google.com/maps?q=${encodeURIComponent(baustelleMapAddress)}&output=embed`}
              className="w-full h-full border-0 pointer-events-none"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-app-surface-muted">
              <MapPin className="w-10 h-10 text-slate-300" />
            </div>
          )}
          {/* Back button */}
          <button
            onClick={goBack}
            type="button"
            className="absolute top-3 left-3 p-2 bg-app-surface/90 backdrop-blur-sm rounded-full shadow-md text-app-text-muted hover:bg-app-surface"
            aria-label="Zurück"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          {/* Aktionen pill */}
          <div className="absolute top-3 right-3">
            <button
              type="button"
              onClick={() => setShowMobileActionsMenu((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-app-surface/90 backdrop-blur-sm rounded-full shadow-md text-app-text-muted text-xs font-semibold hover:bg-app-surface"
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
              Aktionen
            </button>
            {showMobileActionsMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMobileActionsMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 bg-app-surface rounded-lg shadow-lg border border-app-border min-w-[170px] py-1 overflow-hidden">
                  {isLeadWon || isLeadLost ? (
                    <button
                      type="button"
                      onClick={() => { void handleReopenLead(); setShowMobileActionsMenu(false); }}
                      disabled={isSaving}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-app-text-muted hover:bg-app-hover disabled:opacity-50"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Projekt wieder öffnen
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => { void handleGewonnen(); setShowMobileActionsMenu(false); }}
                        disabled={isSaving}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                      >
                        <Check className="w-4 h-4" />
                        Gewonnen
                      </button>
                      <button
                        type="button"
                        onClick={() => { openVerlorenModal(); setShowMobileActionsMenu(false); }}
                        disabled={isSaving}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                        Verloren
                      </button>
                    </>
                  )}
                  <div className="h-px bg-app-surface-muted mx-2 my-1" />
                  <button
                    type="button"
                    onClick={() => { setShowCreateDocumentModal(true); setShowMobileActionsMenu(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-app-text-muted hover:bg-app-hover"
                  >
                    <FilePlus className="w-4 h-4 text-pipedrive-blue" />
                    Dokument erstellen
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Floating contact card — overlaps map at the bottom */}
        <div className="mx-3 -mt-6 relative z-10 bg-app-surface rounded-2xl shadow-lg px-4 py-3">
          <div className="flex items-center gap-3">
            {/* Tappable: opens Kontakt + Baustelle (Angaben bearbeiten) */}
            <button
              type="button"
              onClick={() => setActiveMobileSection('details')}
              className="min-w-0 flex-1 text-left rounded-xl -my-1 -ml-1 pl-1 py-1 pr-2 hover:bg-app-hover active:bg-app-surface-muted/80 transition-colors"
              aria-label="Angaben zum Projekt bearbeiten"
            >
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-bold text-app-text text-[15px] leading-snug">{headerContactName || '—'}</span>
                {terminalStatusLabel && (
                  <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                    isLeadWon
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                      : 'bg-red-100 text-red-800 border-red-200'
                  }`}>
                    {terminalStatusLabel}
                  </span>
                )}
              </div>
              <p className="text-sm text-app-text-subtle mt-0.5">
                {headerContactCompany || lead.contact?.contact_type || '–'}
              </p>
              {baustelleMapAddress && (
                <p className="text-xs text-slate-400 mt-0.5 truncate">{baustelleMapAddress}</p>
              )}
            </button>
            {/* Action icon buttons */}
            <div className="flex items-center gap-2 shrink-0">
              {headerCallPhone && (
                <a
                  href={`tel:${headerCallPhone}`}
                  className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white shadow hover:bg-emerald-700 active:bg-emerald-800"
                  aria-label="Anrufen"
                >
                  <Phone className="w-4 h-4" />
                </a>
              )}
              <a
                href={`https://maps.google.com/?daddr=${encodeURIComponent(baustelleMapAddress || (lead.contact?.address ?? ''))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-pipedrive-blue flex items-center justify-center text-white shadow hover:bg-blue-700 active:bg-blue-800"
                aria-label="Route berechnen"
              >
                <Navigation className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Deal info strip — full width */}
      <div className="mt-4 bg-app-surface overflow-hidden">
        {/* ID + category + status + price row */}
        <div className="px-4 pt-3 pb-1 flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-xs text-app-text-subtle">{lead.display_id}</span>
            <span className="text-slate-300 text-xs">·</span>
            <LeadCategoryPicker
              value={lead.category_key ?? ''}
              categories={leadCategories}
              onChange={(key) => void handleLeadCategoryChange(key)}
              disabled={isSaving || leadCategories.length === 0}
            />
          </div>
          <span className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${
            isLeadWon
              ? 'bg-emerald-600 text-white'
              : isLeadLost
                ? 'bg-red-500 text-white'
                : 'bg-pipedrive-blue text-white'
          }`}>
            {terminalStatusLabel ?? (status || lead.status)}
          </span>
        </div>
        {/* Big title + price on same row */}
        <div className="px-4 pb-3">
          <div className="flex items-baseline justify-between gap-3">
            <div className="min-w-0 flex-1 flex items-start gap-1.5">
              {leadTitleEditing ? (
                <input
                  ref={leadTitleInputMobileRef}
                  type="text"
                  value={leadTitleDraft}
                  onChange={(e) => setLeadTitleDraft(e.target.value)}
                  onBlur={() => void saveLeadTitleFromEdit()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void saveLeadTitleFromEdit();
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelLeadTitleEdit();
                    }
                  }}
                  maxLength={500}
                  disabled={isSaving}
                  className="w-full text-[17px] font-bold text-app-text leading-snug bg-app-surface border border-pipedrive-blue rounded-lg px-2 py-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-pipedrive-blue/30"
                  aria-label="Projekt-Titel bearbeiten"
                />
              ) : (
                <button
                  type="button"
                  onClick={startEditingLeadTitle}
                  className="text-left min-w-0 flex-1 flex items-start gap-1.5 rounded-lg px-1 -mx-1 py-0.5 hover:bg-app-hover focus:outline-none focus:ring-2 focus:ring-pipedrive-blue/30 group"
                  title="Titel bearbeiten"
                >
                  <span className="text-[17px] font-bold text-app-text leading-snug line-clamp-2 min-w-0">
                    {lead.lead_title || '—'}
                  </span>
                  <Pencil className="w-3.5 h-3.5 shrink-0 mt-1 text-slate-400 opacity-60 group-hover:opacity-100" aria-hidden />
                </button>
              )}
            </div>
            {offerNetSum !== null && (
              <span className="shrink-0 text-right" title={projectValueTooltip(projectValue)}>
                <span className="block text-[15px] font-bold text-app-text-muted">{leadHeaderEurFmt.format(offerNetSum)}</span>
                {projectValue ? (
                  <span className="block text-[11px] font-normal leading-tight text-slate-400">
                    {projectValueShortLabel(projectValue)} · netto
                  </span>
                ) : null}
                {projectValueContextText(projectValue) ? (
                  <span className="block text-[11px] font-normal leading-tight text-slate-400 tabular-nums">
                    {projectValueContextText(projectValue)}
                  </span>
                ) : null}
              </span>
            )}
          </div>
        </div>
        {/* Tab strip — attached directly to deal info */}
        <div className="flex border-t border-app-border">
          {([
            { id: 'zusammenfassung' as const, label: 'Überblick', icon: FileText },
            { id: 'angebot' as const, label: 'Angebot', icon: Package },
            { id: 'dokumente' as const, label: 'Dokumente', icon: Paperclip },
            { id: 'kosten' as const, label: 'Kosten', icon: Receipt },
          ] as const).filter(({ id }) => (id !== 'kosten' || showKostenTab) && (id !== 'angebot' || showAngebotTab)).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveMobileSection(id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeMobileSection === id
                  ? 'text-pipedrive-blue border-pipedrive-blue'
                  : 'text-app-text-subtle border-transparent hover:text-app-text-muted'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
